import type React from "react";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../hooks/api/useApi";
import { formatTime } from "../../utils/utils";
import { toast } from "react-toastify";

const ResetPassword = () => {
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [timeLeft, setTimeLeft] = useState(10 * 60);
  const navigate = useNavigate();
  const api = useApi();

  useEffect(() => {
    const username = localStorage.getItem("username");
    if (!username) {
      navigate("/login");
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);
          navigate("/login");
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  const validateForm = () => {
    if (!otp.trim()) {
      setError("Please enter the OTP");
      return false;
    }
    if (!password.trim()) {
      setError("Please enter a new password");
      return false;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!validateForm()) {
      return;
    }

    const username = localStorage.getItem("username");
    if (!username) {
      navigate("/login");
      return;
    }

    const result = await api.post("access/resetpassword", {
      username: username,
      resetToken: otp.trim(),
      password: password.trim(),
      confirmPassword: confirmPassword.trim(),
    //   passwordHint: "me",
    });

    if (result.statusCode === "00") {
      toast("password reset succesfully");
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } else {
      setError(result.message || "Invalid or Expired OTP");
      setTimeout(() => {
        navigate("/login");
      }, 2500);
    }
  };

  return (
    <section className="flex w-full items-center justify-center md:p-8 lg:p-16">
      <motion.div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg sm:p-8"
        initial={{ x: 100, opacity: 0, scale: 0.8 }}
        animate={{ x: 0, opacity: 1, scale: 1 }}
        transition={{
          type: "spring",
          stiffness: 100,
          damping: 15,
          delay: 0.1,
        }}
      >
        <div className="mb-6">
          <h1 className="text-xl font-medium text-gray-900">
            Reset Your Password
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Enter the OTP sent to your email and create a new password
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-500">
            {error}
          </div>
        )}

        {api.error && !error && (
          <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-500">
            {api.error.message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label
              htmlFor="otp"
              className="block text-sm font-medium text-gray-700"
            >
              OTP Code
            </label>
            <input
              id="otp"
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
              className="w-full rounded-md border border-gray-300 px-3 py-3 text-gray-900 placeholder-gray-400 "
              placeholder="Enter OTP code"
            />
          </div>

          <div className="space-y-2">
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700"
            >
              New Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full rounded-md border border-gray-300 px-3 py-3 pr-10 text-gray-900 placeholder-gray-400 "
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-gray-700"
            >
              Confirm Password
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full rounded-md border border-gray-300 px-3 py-3 pr-10 text-gray-900 placeholder-gray-400 "
                placeholder="Confirm new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <motion.button
            type="submit"
            disabled={api.isLoading || !otp || !password || !confirmPassword}
            className="w-full rounded-md bg-red-600 py-3 text-center font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {api.isLoading ? (
              <span className="flex items-center justify-center">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Resetting Password...
              </span>
            ) : (
              "Reset Password"
            )}
          </motion.button>

          <div className="text-center text-sm text-gray-500">
            OTP expires in{" "}
            <span className="font-medium text-red-600">
              {formatTime(timeLeft)}
            </span>
          </div>
        </form>
      </motion.div>
    </section>
  );
};

export default ResetPassword;
